import React, { useState, useEffect } from 'react';
import { Star, Clock, Users } from 'lucide-react';
import { Button } from '../ui/button';
import { useNavigate } from 'react-router-dom';
import { getTutors } from '../../services/api';

export const FeaturedTutors = () => {
  const navigate = useNavigate();
  const [tutors, setTutors] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFeaturedTutors();
  }, []);

  const fetchFeaturedTutors = async () => {
    try {
      const data = await getTutors({ sort_by: 'rating' });
      const tutorList = Array.isArray(data) ? data : [];
      setTutors(tutorList.slice(0, 6)); // Get top 6 tutors
    } catch (error) {
      console.error('Error fetching tutors:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="text-gray-600">Loading featured tutors...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
            Featured Tutors
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Connect with our top-rated and experienced tutors
          </p>
        </div>

        {/* Tutors Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {tutors.map((tutor) => (
            <div
              key={tutor.id}
              className="bg-white rounded-xl shadow-sm hover:shadow-xl transition-all border border-gray-100 overflow-hidden group"
            >
              {/* Tutor Image */}
              <div className="relative h-48 overflow-hidden">
                <img
                  src={tutor.image}
                  alt={tutor.name}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                />
                <div className="absolute top-4 right-4 bg-white px-3 py-1 rounded-full flex items-center space-x-1 shadow-lg">
                  <Star className="h-4 w-4 text-yellow-500 fill-current" />
                  <span className="text-sm font-bold text-gray-900">{tutor.rating}</span>
                </div>
              </div>

              {/* Tutor Info */}
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-1">
                  {tutor.name}
                </h3>
                <p className="text-blue-600 font-medium mb-3">{tutor.subject}</p>
                <p className="text-gray-600 text-sm mb-4 line-clamp-2">{tutor.bio}</p>

                {/* Stats */}
                <div className="flex items-center justify-between mb-4 text-sm text-gray-600">
                  <div className="flex items-center space-x-1">
                    <Clock className="h-4 w-4" />
                    <span>{tutor.experience}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Users className="h-4 w-4" />
                    <span>{tutor.students} students</span>
                  </div>
                </div>

                {/* Price and CTA */}
                <div className="flex items-center justify-between pt-4 border-t">
                  <div>
                    <p className="text-2xl font-bold text-gray-900">₹{tutor.hourly_rate}</p>
                    <p className="text-xs text-gray-500">per hour</p>
                  </div>
                  <Button
                    onClick={() => navigate(`/tutor/${tutor.id}`)}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    Book Now
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* View All Button */}
        <div className="text-center mt-12">
          <Button
            size="lg"
            variant="outline"
            onClick={() => navigate('/find-tutor')}
            className="border-2 border-blue-600 text-blue-600 hover:bg-blue-50"
          >
            View All Tutors
          </Button>
        </div>
      </div>
    </div>
  );
};
